import React, { useMemo } from 'react';
import { useTEF } from 'src/tef/components/providers/TEFProvider';
import { cn } from 'src/tef/components/utils/cn';
import { forwardProps } from 'src/tef/components/utils/forwardProps';
import type { BaseComponentState, ForwardedHTMLProps } from 'src/tef/components/utils/types';

type TextElement =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'span'
  | 'label'
  | 'legend'
  | 'caption'
  | 'time'
  | 'address';

const typographySizeMap = {
  xs: 'textXs',
  sm: 'textSm',
  base: 'textBase',
  lg: 'textLg',
  xl: 'textXl',
  '2xl': 'text2xl',
  '3xl': 'text3xl',
} as const;

type TypographySize = keyof typeof typographySizeMap;

type TextColor = 'primary' | 'secondary' | 'muted' | 'success' | 'warning' | 'error' | 'inherit';

interface TypographyTextProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'color'>, ForwardedHTMLProps, BaseComponentState {
  children: React.ReactNode;
  variant?: { type?: TextElement; size?: TypographySize };
  className?: string;
  as?: TextElement;
  color?: TextColor;
  weight?: 'normal' | 'medium' | 'semibold' | 'bold';
  align?: 'left' | 'center' | 'right' | 'justify';
  truncate?: boolean;
  lineClamp?: 1 | 2 | 3 | 4 | 5 | 6;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  uppercase?: boolean;
  capitalize?: boolean;
  nowrap?: boolean;
  tone?: 'default' | 'formal';
}

const colorMap: Record<TextColor, string> = {
  primary: 'text-slate-900 dark:text-slate-100',
  secondary: 'text-slate-600 dark:text-slate-400',
  muted: 'text-slate-400 dark:text-slate-400',
  success: 'text-green-600 dark:text-green-400',
  warning: 'text-amber-600 dark:text-amber-400',
  error: 'text-red-600 dark:text-red-400',
  inherit: 'text-inherit',
};

const weightMap: Record<string, string> = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

const alignMap: Record<string, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
  justify: 'text-justify',
};

const lineClampMap: Record<number, string> = {
  1: 'line-clamp-1',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
  5: 'line-clamp-5',
  6: 'line-clamp-6',
};

const HEADING_TYPES = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];

export const TypographyText = React.forwardRef<HTMLElement, TypographyTextProps>(
  (
    {
      children,
      variant,
      className,
      as,
      color,
      weight,
      align,
      truncate = false,
      lineClamp,
      italic = false,
      underline = false,
      strikethrough = false,
      uppercase = false,
      capitalize = false,
      nowrap = false,
      tone = 'default',
      loading = false,
      disabled = false,
      error = false,
      ...props
    },
    ref
  ) => {
    const tef = useTEF();
    const forwardedProps = forwardProps(props);

    const textType = variant?.type || as || 'p';
    const textSize: TypographySize = variant?.size || 'base';

    const isHeading = HEADING_TYPES.includes(textType);
    const isLabel = textType === 'label';

    const textClasses = useMemo(() => {
      let sizeClass: string;
      let defaultWeightClass = '';
      let lineHeightClass = '';

      if (isHeading && tef.typography.heading) {
        const headingStyle = tef.typography.heading[textType as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'];
        sizeClass = headingStyle.size;
        defaultWeightClass = headingStyle.weight;
        lineHeightClass = headingStyle.lineHeight;
      } else {
        const sizeKey = typographySizeMap[textSize];
        sizeClass = tef.typography[sizeKey];
      }

      const colorClass = color ? colorMap[color] : error ? colorMap.error : tef.colors.foreground;

      const formalClass =
        tone === 'formal'
          ? isHeading
            ? 'font-serif tracking-tight'
            : isLabel
              ? 'text-[11px] font-semibold uppercase tracking-[0.12em]'
              : ''
          : '';

      return cn(
        sizeClass,
        weight ? weightMap[weight] : defaultWeightClass,
        lineHeightClass,
        colorClass,
        align && alignMap[align],
        truncate && 'truncate',
        lineClamp && lineClampMap[lineClamp],
        italic && 'italic',
        underline && 'underline',
        strikethrough && 'line-through',
        uppercase && 'uppercase',
        capitalize && 'capitalize',
        nowrap && 'whitespace-nowrap',
        loading && 'animate-pulse bg-slate-200 dark:bg-slate-700 rounded',
        disabled && 'opacity-50',
        formalClass,
        className
      );
    }, [
      tef,
      textType,
      textSize,
      isHeading,
      isLabel,
      tone,
      color,
      weight,
      align,
      truncate,
      lineClamp,
      italic,
      underline,
      strikethrough,
      uppercase,
      capitalize,
      nowrap,
      loading,
      disabled,
      error,
      className,
    ]);

    const Component = textType as React.ElementType;

    if (loading && !children) {
      return (
        <Component
          ref={ref}
          className={cn(textClasses, 'min-h-[1em] min-w-[100px]')}
          aria-busy="true"
          {...forwardedProps}
        >
          &nbsp;
        </Component>
      );
    }

    return (
      <Component ref={ref} className={textClasses} aria-disabled={disabled || undefined} {...forwardedProps}>
        {children}
      </Component>
    );
  }
);

TypographyText.displayName = 'TypographyText';

export default TypographyText;
